<template>
  <div class="row">
    <div class="preset-themes">
      Font
      <Dropdown
          v-model="themeFontName"
          :options="groupedFonts"
          optionGroupLabel="label"
          optionGroupChildren="items"
          placeholder="Select a font"
          style="width: 200px"
      >
        <template #optiongroup="slotProps">
          <div class="flex align-items-center">
            <div style="color:rgb(120,132,152)">{{ slotProps.option.label }}</div>
          </div>
        </template>
        <template #option="slotProps">
          <span :style="{ fontFamily: slotProps.option }">{{ slotProps.option }}</span>
        </template>
      </Dropdown>
    </div>

    <div class="preset-themes">
      Theme Colors
      <div class="row" style="width: 200px" v-for="(color, index) in theme.themeColors" :key="index">
        <div class="color-btn-wrap" style="flex: 4;">
          <ColorButton @click.stop="$refs.themeColorPickerVisible[index].toggle($event)" :color="color" style="width: 150px;"/>
          <div class="delete-color-btn" @click.stop="deleteThemeColor(index)" style="width: 20px;">
            <IconCloseSmall/>
          </div>
        </div>
        <OverlayPanel ref="themeColorPickerVisible">
          <ThemeColorPicker
              :modelValue="color"
              @update:modelValue="(value) => { updateTheme(value, index); }"
          />
        </OverlayPanel>
      </div>
      <Button
          class="btn"
          style="flex: 3;"
          @click.stop="addThemeColor($event)"
      >
        <IconPlus class="btn-icon"/>
        Add theme color
      </Button>
    </div>
    <Divider />
    <div class="font-row" style="margin-top: 10px;">
      <span>Import Fonts from your website</span>
      <InputText
          style="flex:3;"
          v-model="customerWebpageUrl"
          placeholder="Enter a website URL (e.g., example.com or https://www.example.com)"
          @keydown.enter="websiteFontImport"
      ></InputText>
      <Button
          type="button"
          class="submit"
          @click="websiteFontImport()"
          :loading="fetchingFonts"
          icon="pi pi-cloud-upload"
      />
    </div>
    <div v-if="showMessage" :class="['feedback-message', messageType]">
      {{ infoMessage }}
    </div>
  </div>
</template>
<script setup lang="ts">
import {computed, onMounted, ref } from "vue";

import Button from "primevue/button";
import {storeToRefs} from "pinia";
import {useSlidesStore} from "@/store";
import ThemeColorPicker from "@/components/ThemeColorPicker/index.vue";
import ColorButton from "@/views/Editor/Toolbar/common/ColorButton.vue";
import useMicroservice from "@/hooks/useMicroservice";

const { importFontsFromWebsite } = useMicroservice()

onMounted( () => {
  if (theme.value.fontFaces) {
    customFonts.value = [
      ...new Set(theme.value.fontFaces.map(font => font.fontFamily))
    ]
  }
})

const customFonts = ref()
const groupedFonts = computed(() => {
  const groups = []

  // Only showing 'Custom Fonts' if config has any to list
  if (customFonts.value && customFonts.value.length > 0) {
    groups.push({
      label: 'Custom Fonts',
      items: customFonts.value
    })
  }

  groups.push({
    label: 'Default Fonts',
    items: [
      "Arial", "Verdana", "Helvetica", "Tahoma", "Trebuchet MS",
      "Georgia", "Times New Roman", "Palatino Linotype", "Garamond",
      "Courier New", "Lucida Console", "Impact", "Comic Sans MS"
    ]
  })

  return groups
})

const customerWebpageUrl = ref('')
const fetchingFonts = ref(false)
const infoMessage = ref('')
const showMessage = ref(false)
const messageType = ref('')

const { theme } = storeToRefs(useSlidesStore())

const themeFontName = computed({
  get() {
    return theme.value.fontName ? theme.value.fontName : ''
  },
  set(val: any) {
    theme.value.fontName = val
  }
})

const addThemeColor = (e) => theme.value.themeColors.push('')

const updateTheme = (color: string, index: number) => {
  theme.value.themeColors = theme.value.themeColors.map((c, i) => i === index ? color : c)
  document.documentElement.style.setProperty(`--theme-${index}`, color)
}


// Use preset color schemes
const applyPresetTheme = (colors: string[], index: number) => {
  // selectedWidget.value.themeColor = colors.slice(0, index + 1)
  // presetThemesVisible.value = false
}

// Delete theme color
const deleteThemeColor = (index: number) => {
  theme.value.themeColors = theme.value.themeColors.filter((c, i) => i !== index)
  document.documentElement.style.setProperty(`--theme-${index}`, '')
}

const formatUrl = (url: string) => {
  if (!url) return ''
  if (!url.startsWith('http')) {
    return `https://www.${url.replace(/^(https?:\/\/)?(www\.)?/, '')}`
  }
  return url
}

const websiteFontImport = async () => {
  fetchingFonts.value = true
  try {
    const url = formatUrl(customerWebpageUrl.value)
    const importedFonts = await importFontsFromWebsite(url)

    if (importedFonts.length === 0) {
      setMessage(`No new fonts have been added.`, 'info')
    } else {
      theme.value.fontFaces ? theme.value.fontFaces.push(...importedFonts) : theme.value.fontFaces = importedFonts

      customFonts.value = [ ...new Set(theme.value.fontFaces.map(font => font.fontFamily))]

      theme.value.fontFaces.forEach(loadFont)

      setMessage(`Font(s) added: ${customFonts.value}`, 'success')
    }

    fetchingFonts.value = false
    customerWebpageUrl.value = ''
  } catch {
    fetchingFonts.value = false

    setMessage('There was an issue importing fonts from this website. Try another.', 'error')
  }
}

const setMessage = (message: string, type: string, duration = 5000) => {
  infoMessage.value = message
  messageType.value = type
  showMessage.value = true

  setTimeout(() => {
    resetMessageRefs()
  }, duration)
}

const resetMessageRefs = () => {
  infoMessage.value = ''
  messageType.value = ''
  showMessage.value = false
}

const loadFont = (font) => {
  const fontFace = new FontFace(font.fontFamily, `url(${font.src})`, {
    weight: font.fontWeight,
    style: font.fontStyle,
  })

  fontFace.load().then((loadedFont) => {
    document.fonts.add(loadedFont)
  }).catch((error) => {
    console.error('Failed to load font:', error)
  })
}


</script>
<style lang="scss" scoped>
.i-icon {
  z-index: 1;
}

:deep( .p-button.p-component ) {
  justify-content: center;
}

.group {
  margin-bottom: 10px;
}

.chart-style-panel {
  user-select: none;
  padding-top: 20px;
}

.row {
  width: 100%;
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
}

.full-width-btn {
  width: 100%;
}

.btn-icon {
  margin-right: 3px;
}

.color-btn-wrap {
  position: relative;
}

.delete-color-btn {
  position: absolute;
  width: 30px;
  right: 2px;
  top: 2px;
  bottom: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #fff;
  cursor: pointer;
}

.preset-themes {
  width: 300px;
  display: flex;
  margin-bottom: 20px;
  flex-direction: column;

  @include flex-grid-layout();
}

.preset-theme {
  display: flex;
  cursor: pointer;

  @include flex-grid-layout-children(2, 48%);
}

.preset-theme-color {
  width: 20px;
  height: 20px;

  &.select {
    transform: scale(1.2);
    transition: transform $transitionDelayFast;
  }
}

.chk-box {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.chk-box label {
  margin-left: 5px; /* Adjust the gap as needed */
}

.btn-group {
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep( #padding-input > .p-inputtext.p-component.p-inputnumber-input ) {
  margin-right: 5px;
  width: 70px;
  margin-top: 20px;
}

.chart-padding-label {
  top: 0;
  position:absolute;
}

.dimension-input :deep(.p-inputtext) { width: 110px; }

span.label { display: inline-block; width: 60px }
span.label-long { display: inline-block; width: 100px }
span.label-x-long { display: inline-block; width: 120px }

.style-section { margin-top: 20px; margin-bottom: 20px;}
.style-row { margin-top: 5px; width: 350px; align-items: center; display: flex  }

:deep(.p-highlight span) { z-index: 100; }
:deep(.p-selectbutton .p-button::before) {
  transition: none !important;
}

:deep(.p-tag) { margin-left: 5px;}
:deep(.p-tag-value) { line-height: 1}
:deep(.chk-box) {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

:deep(.chk-box label) {
  margin-left: 5px;
}

:deep(.p-button){
  background: #fff;
  border: 1px solid #ccc;
  color: black;
}

.dimension-input :deep(.p-inputtext) {
  width: 50px;
}

.smaller-switch {
  transform: scale(0.6);
  display: inline-block;
  vertical-align: middle;
  margin-right: 10px;
}

.border-container { width: 350px; display: flex; align-items: center; color: #666 }
.trash { margin-left: 5px; }
:deep(.p-panel-header) { padding-left: 8px !important; padding-right: 8px !important; background: transparent;
}

.border-inputs :deep(.p-inputtext) {
  width: 50px;
}

:deep(.p-hidden-accessible) {
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
}

:deep(.p-button.submit){
  background: #fff;
  border: 1px solid #ccc;
  color: black;
  height: 40px;
}

.font-row {
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
}

.feedback-message {
  position: absolute;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  z-index: 10;
  bottom: 0;
  right: 20px;
  width: auto;
  animation: fadeIn 0.3s ease-in-out;
}

.feedback-message.success {
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}

.feedback-message.info {
  background-color: #e8e8e8;
  color: #333;
  border: 1px solid #d5d5d5;
}

.feedback-message.error {
  background-color: #ffebee;
  color: #c62828;
  border: 1px solid #ef9a9a;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>